import { X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

import { useFocusTrap } from "@/lib/use-focus-trap";

/**
 * Modal backdrop rendered via a portal to `document.body` so the
 * overlay escapes any ancestor that would otherwise act as a fixed-
 * positioning containing block (sticky headers with backdrop-filter,
 * any transform / filter / contain up the tree, …).
 *
 * Children are centered inside the backdrop on sm+ and bottom-aligned
 * on narrow viewports.
 *
 * **Close semantics:** explicit only. The backdrop does NOT close on
 * click - user feedback (2026-04-24) said "make sure they don't just
 * close by clicking outside … we should explicitly close when done".
 * Admins lose form data on an accidental click-through otherwise.
 * Close paths remaining: the always-visible top-right ✕ button, the
 * dialog's own Cancel / Close button, and the ESC key (standard
 * accessibility affordance - not a click, can't fire accidentally).
 */
export function ModalOverlay(props: {
  ariaLabel: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element | null {
  // Focus trap (WCAG 2.1.2) + Esc-to-close handled inside the hook.
  const dialogRef = useFocusTrap<HTMLDivElement>(true, props.onClose);

  // Lock body scroll while open. (Esc handling lives in useFocusTrap.)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Two-div structure so tall dialogs can scroll past the viewport:
  //   - outer: full-viewport dimmed backdrop that is itself the scroll
  //     container (overflow-y-auto). Does NOT close on click - see
  //     docstring.
  //   - inner: min-h-full flex wrapper that centers short dialogs on
  //     sm+ and bottom-aligns on narrow screens, while letting tall
  //     dialogs grow naturally and push the backdrop to scroll.
  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={props.ariaLabel}
      className="fixed inset-0 z-[60] overflow-y-auto bg-black/40"
    >
      <div className="flex min-h-full items-end justify-center p-3 sm:items-center">
        {/* Wrap the dialog body so the close affordance can sit on
            the modal panel itself - not on the viewport corner. The
            X used to be ``fixed right-3 top-3`` which floated near
            the page edge and read as a stray icon rather than a
            control attached to the dialog. Anchored relative to the
            children now so any consumer panel ``rounded-lg ... p-6``
            etc. picks up an X in its top-right. */}
        <div className="relative">
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            className="absolute right-2 top-2 z-[70] rounded-full bg-white/90 p-1.5 text-slate-500 shadow-sm hover:bg-white hover:text-slate-900"
          >
            <X className="h-4 w-4" />
          </button>
          {props.children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
